import { dedupe, ensureCCW, type Pt } from "../math/polygon";
import type { RcCantileverGeometry, StructureType } from "../types/input";

const r6 = (v: number): number => Math.round(v * 1e6) / 1e6;

/** Betonarme konsol kesitin karakteristik x koordinatları (burun ucu x = 0, taban y = 0). */
export function rcFrame(g: RcCantileverGeometry) {
  const tf = g.base.thickness, toe = g.base.toeLength, heel = g.base.heelLength;
  const { height: hs, topThickness: tt, bottomThickness: tb } = g.stem;
  const xStemFront = toe, xStemBack = toe + tb;
  const xTopFront = g.stem.taperSide === "front" ? toe + (tb - tt) : toe;
  const xTopBack = xTopFront + tt;
  return { tf, toe, heel, hs, tt, tb, B: toe + tb + heel, xStemFront, xStemBack, xTopFront, xTopBack, yTop: tf + hs };
}

/** T kesit çokgeni (CCW, burun (0,0) ilk eleman). Diş dahil değildir. */
export function rcToPolygon(g: RcCantileverGeometry): Pt[] {
  const f = rcFrame(g);
  const pts: Pt[] = [
    { x: 0, y: 0 }, { x: f.B, y: 0 }, { x: f.B, y: f.tf }, { x: f.xStemBack, y: f.tf }, { x: f.xTopBack, y: f.yTop },
    { x: f.xTopFront, y: f.yTop }, { x: f.xStemFront, y: f.tf }, { x: 0, y: f.tf },
  ].map((p) => ({ x: r6(p.x), y: r6(p.y) }));
  const ccw = ensureCCW(dedupe(pts));
  const i0 = ccw.findIndex((p) => Math.abs(p.x) < 1e-9 && Math.abs(p.y) < 1e-9);
  return i0 > 0 ? [...ccw.slice(i0), ...ccw.slice(0, i0)] : ccw;
}

/** Kayma dişi çokgeni (taban altında, y < 0) ya da null. */
export function rcKeyPolygon(g: RcCantileverGeometry): Pt[] | null {
  if (!g.key) return null;
  const x0 = g.key.offsetFromToe, x1 = x0 + g.key.width;
  return [{ x: x0, y: -g.key.depth }, { x: x1, y: -g.key.depth }, { x: x1, y: 0 }, { x: x0, y: 0 }];
}

/** Gövde arka yüzü: kottaki x (taban üstünden tepeye doğrusal). */
export function rcStemBackXAt(g: RcCantileverGeometry, y: number): number {
  const f = rcFrame(g);
  const t = Math.min(1, Math.max(0, (y - f.tf) / f.hs));
  return f.xStemBack + (f.xTopBack - f.xStemBack) * t;
}
/** Gövde ön yüzü x (kotta). */
export function rcStemFrontXAt(g: RcCantileverGeometry, y: number): number {
  const f = rcFrame(g);
  const t = Math.min(1, Math.max(0, (y - f.tf) / f.hs));
  return f.xStemFront + (f.xTopFront - f.xStemFront) * t;
}
/** Gövde kalınlığı (kotta). */
export const rcStemThicknessAt = (g: RcCantileverGeometry, y: number): number => rcStemBackXAt(g, y) - rcStemFrontXAt(g, y);

export function structureTypeOf(g: { mode: string; counterforts?: unknown }): StructureType {
  if (g.mode !== "rc-cantilever") return "masonry-gravity";
  return g.counterforts ? "rc-counterfort" : "rc-cantilever";
}

/** Payanda kesit profili (x–y, duvar boyuna dik düzlemde): topuk üstünde gövde arkasından topuk ucuna, tepede `topWidth` genişliğinde (0 = üçgen). Payanda yoksa null. */
export function rcCounterfortProfile(g: RcCantileverGeometry): Pt[] | null {
  const cf = g.counterforts;
  if (!cf) return null;
  const f = rcFrame(g);
  const xTop = rcStemBackXAt(g, f.yTop);
  return [{ x: f.xStemBack, y: f.tf }, { x: f.B, y: f.tf }, { x: Math.min(f.B, xTop + cf.topWidth), y: f.yTop }, { x: xTop, y: f.yTop }];
}

/** Payanda eksen konumları duvar boyunca (z, 0…L): ilk payanda yarım aralıkta, sonra `spacing` adımla. Payanda yoksa boş. */
export function rcCounterfortPositions(g: RcCantileverGeometry, L: number): number[] {
  const cf = g.counterforts;
  if (!cf || !(L > 0)) return [];
  const out: number[] = [];
  for (let z = cf.spacing / 2; z < L; z += cf.spacing) out.push(Math.round(z * 1e6) / 1e6);
  return out;
}
